import { useAuth } from "@/contexts/AuthContext";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import { DashboardSidebar } from "@/components/DashboardSidebar";
import { GraftonChat } from "@/components/GraftonChat";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { BarChart3, Lightbulb, ClipboardList, Users } from "lucide-react";
import { SalesOverviewTab } from "@/components/sales/SalesOverviewTab";
import { ActionPlanTab } from "@/components/sales/ActionPlanTab";
import { AIAdviceTab } from "@/components/sales/AIAdviceTab";
import { ProspectsTab } from "@/components/sales/ProspectsTab";

export default function SalesDashboard() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [kpis, setKpis] = useState<{ label: string; value: string }[]>([]);
  const [activeTab, setActiveTab] = useState(searchParams.get("tab") || "overview");

  useEffect(() => {
    if (!loading && !user) navigate("/auth");
  }, [user, loading, navigate]);

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-background text-foreground">Chargement…</div>;
  if (!user) return null;

  return (
    <SidebarProvider>
      <div className="min-h-screen flex w-full bg-background">
        <DashboardSidebar />
        <div className="flex-1 flex flex-col min-w-0">
          <header className="h-14 flex items-center justify-between border-b border-border px-4">
            <div className="flex items-center gap-3">
              <SidebarTrigger />
              <h1 className="text-lg font-semibold text-foreground">Dashboard Ventes</h1>
              <Badge variant="outline" className="border-primary/40 text-primary text-xs">LIVE</Badge>
            </div>
          </header>

          <main className="flex-1 overflow-y-auto p-4 md:p-6">
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="mb-6 bg-secondary/50">
                <TabsTrigger value="overview" className="gap-1.5 text-xs">
                  <BarChart3 className="h-3.5 w-3.5" />Vue d'ensemble
                </TabsTrigger>
                <TabsTrigger value="prospects" className="gap-1.5 text-xs">
                  <Users className="h-3.5 w-3.5" />Prospects
                </TabsTrigger>
                <TabsTrigger value="action-plan" className="gap-1.5 text-xs">
                  <ClipboardList className="h-3.5 w-3.5" />Plan d'actions
                </TabsTrigger>
                <TabsTrigger value="ai-advice" className="gap-1.5 text-xs">
                  <Lightbulb className="h-3.5 w-3.5" />Conseils IA
                </TabsTrigger>
              </TabsList>

              <TabsContent value="overview">
                <SalesOverviewTab onKpis={setKpis} />
              </TabsContent>
              <TabsContent value="prospects">
                <ProspectsTab />
              </TabsContent>

              <TabsContent value="action-plan">
                <ActionPlanTab />
              </TabsContent>

              <TabsContent value="ai-advice">
                <AIAdviceTab />
              </TabsContent>
            </Tabs>
          </main>
        </div>
        <GraftonChat
          variant="sales"
          context={{ dashboard: "ventes", tab: activeTab, kpis }}
          subtitle="Directeur commercial — pipeline en temps réel"
          welcomeMessage="Posez-moi vos questions sur les ventes, les objectifs ou demandez une action commerciale."
          quickPrompts={[
            "Quels clients relancer cette semaine ?",
            "Analyse mes objectifs du mois",
            "Propose un plan d'action commercial",
            "Quels sont mes points de vigilance ?",
          ]}
        />
      </div>
    </SidebarProvider>
  );
}
